import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MRPView = ({ formData, setFormData, selectedPlants = [], readOnly = false }) => {
  // Master data
  const [mrpTypes, setMrpTypes] = useState([]);
  const [mrpControllers, setMrpControllers] = useState([]);
  const [lotSizingProcedures, setLotSizingProcedures] = useState([]);
  const [schedulingMarginKeys, setSchedulingMarginKeys] = useState([]);
  const [periodIndicators, setPeriodIndicators] = useState([]);
  const [forecastModels, setForecastModels] = useState([]);
  const [plants, setPlants] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Use formData.mrp directly instead of local state
  const mrpData = formData.mrp || {
    plant_id: '',
    storage_location_id: '',
    mrp_type_id: '',
    mrp_controller_id: '',
    lot_sizing_procedure_id: '',
    fixed_lot_size: '',
    procurement_type: '',
    inhouse_production_time: '',
    scheduling_margin_key_id: '',
    period_indicator: '',
    forecast_model_id: '',
    hist_periods: '',
    forecast_periods: '',
    tracking_limit: '',
    selection_procedure: '',
    period_ind_shelf_life: ''
  };

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    // Load storage locations when plant is selected
    if (mrpData.plant_id) {
      loadStorageLocations(mrpData.plant_id);
    } else {
      setStorageLocations([]);
    }
  }, [mrpData.plant_id]);

  const loadMasterData = async () => {
    try {
      const [
        mrpTypesRes,
        mrpControllersRes,
        lotSizingProceduresRes,
        schedulingMarginKeysRes,
        periodIndicatorsRes,
        forecastModelsRes,
        plantsRes
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/mrp-types`),
        axios.get(`${BACKEND_URL}/api/mrp-controllers`),
        axios.get(`${BACKEND_URL}/api/lot-sizing-procedures`),
        axios.get(`${BACKEND_URL}/api/scheduling-margin-keys`),
        axios.get(`${BACKEND_URL}/api/period-indicators`),
        axios.get(`${BACKEND_URL}/api/forecast-models`),
        axios.get(`${BACKEND_URL}/api/material-management/plants`)
      ]);

      setMrpTypes(mrpTypesRes.data || []);
      setMrpControllers(mrpControllersRes.data || []);
      setLotSizingProcedures(lotSizingProceduresRes.data || []);
      setSchedulingMarginKeys(schedulingMarginKeysRes.data || []);
      setPeriodIndicators(periodIndicatorsRes.data || []);
      setForecastModels(forecastModelsRes.data || []);
      setPlants(plantsRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  const loadStorageLocations = async (plantId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/material-management/storage-locations`);
      const allStorageLocations = response.data || [];
      // Filter storage locations by plant
      const filtered = allStorageLocations.filter(sl => sl.plant_id === plantId);
      setStorageLocations(filtered);
    } catch (error) {
      console.error('Error loading storage locations:', error);
      toast.error('Failed to load storage locations');
      setStorageLocations([]);
    }
  };

  const handleFieldChange = (field, value) => {
    // Update parent formData directly
    setFormData(prev => ({
      ...prev,
      mrp: {
        ...prev.mrp,
        [field]: value
      }
    }));
  };

  // Get filtered plants based on selectedPlants from parent
  const getAvailablePlants = () => {
    if (selectedPlants.length === 0) {
      // No plants selected yet in any view, show all plants
      return plants;
    }
    // Filter to only show plants that were selected in other views
    return plants.filter(p => selectedPlants.includes(p.plant_id));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-10 h-10 animate-spin text-orange-600 mb-3" />
        <p className="text-sm text-gray-600">Loading MRP master data...</p>
      </div>
    );
  }

  const availablePlants = getAvailablePlants();
  const plantConsistencyWarning = selectedPlants.length > 0;

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-lg font-semibold text-gray-900">MRP Data</h3>
        <p className="text-sm text-gray-600 mt-1">
          Material Requirements Planning configuration for this material
        </p>
      </div>

      {/* Plant Consistency Warning */}
      {plantConsistencyWarning && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-orange-900">Plant Consistency Enforced</h4>
            <p className="text-sm text-orange-700 mt-1">
              Plant selection is restricted to plants already selected in other views ({selectedPlants.join(', ')}) to maintain data consistency across all material views.
            </p>
          </div>
        </div>
      )}

      {/* Plant and Storage Location Selection */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-orange-900 mb-3">Plant & Storage Location *</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Plant *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={mrpData.plant_id}
              onChange={(e) => {
                handleFieldChange('plant_id', e.target.value);
                handleFieldChange('storage_location_id', ''); // Reset storage location
              }}
            >
              <option value="">Select Plant</option>
              {availablePlants.map(p => (
                <option key={p.plant_id} value={p.plant_id}>
                  {p.plant_id} - {p.plant_name}
                </option>
              ))}
            </select>
            {availablePlants.length === 0 && selectedPlants.length > 0 && (
              <p className="text-xs text-red-600 mt-1">No valid plants available. Please check plant selection in other views.</p>
            )}
          </div>
          <div>
            <Label>Storage Location *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={mrpData.storage_location_id}
              onChange={(e) => handleFieldChange('storage_location_id', e.target.value)}
              disabled={!mrpData.plant_id}
            >
              <option value="">
                {mrpData.plant_id ? 'Select Storage Location' : 'Select Plant First'}
              </option>
              {storageLocations.map(sl => (
                <option key={sl.storage_location_id} value={sl.storage_location_id}>
                  {sl.storage_location_id} - {sl.name}
                </option>
              ))}
            </select>
            {mrpData.plant_id && storageLocations.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No storage locations found for this plant</p>
            )}
          </div>
        </div>
      </div>

      {/* MRP Planning Section */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">MRP Planning Parameters</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label>MRP Type</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={mrpData.mrp_type_id}
              onChange={(e) => handleFieldChange('mrp_type_id', e.target.value)}
            >
              <option value="">Select MRP Type</option>
              {mrpTypes.map(mt => (
                <option key={mt.mrp_type_id} value={mt.mrp_type_id}>
                  {mt.mrp_type_id} - {mt.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Planning strategy code</p>
          </div>
          <div>
            <Label>MRP Controller</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={mrpData.mrp_controller_id}
              onChange={(e) => handleFieldChange('mrp_controller_id', e.target.value)}
            >
              <option value="">Select MRP Controller</option>
              {mrpControllers.map(mc => (
                <option key={mc.mrp_controller_id} value={mc.mrp_controller_id}>
                  {mc.mrp_controller_id} - {mc.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Responsible planner</p>
          </div>
          <div>
            <Label>Lot Sizing Procedure *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={mrpData.lot_sizing_procedure_id}
              onChange={(e) => handleFieldChange('lot_sizing_procedure_id', e.target.value)}
            >
              <option value="">Select Lot Sizing Procedure</option>
              {lotSizingProcedures.map(lsp => (
                <option key={lsp.lot_sizing_procedure_id} value={lsp.lot_sizing_procedure_id}>
                  {lsp.lot_sizing_procedure_id} - {lsp.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Lot size calculation method</p>
          </div>
        </div>
      </div>

      {/* Lot Size & Procurement */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Lot Size & Procurement</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label>Fixed Lot Size</Label>
            <Input
              type="number"
              min="0"
              step="0.001"
              value={mrpData.fixed_lot_size}
              onChange={(e) => handleFieldChange('fixed_lot_size', e.target.value)}
              placeholder="Optional"
            />
            <p className="text-xs text-gray-500 mt-1">Must be greater than 0 if lot size is fixed</p>
          </div>
          <div>
            <Label>Procurement Type *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={mrpData.procurement_type}
              onChange={(e) => handleFieldChange('procurement_type', e.target.value)}
            >
              <option value="">Select Procurement Type</option>
              <option value="E">E - In-house Production</option>
              <option value="F">F - External Procurement</option>
              <option value="X">X - Both</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">How material is procured</p>
          </div>
          <div>
            <Label>In-house Production Time (Days)</Label>
            <Input
              type="number"
              min="0"
              max="999"
              value={mrpData.inhouse_production_time}
              onChange={(e) => handleFieldChange('inhouse_production_time', e.target.value)}
              placeholder="For E/X procurement"
            />
            <p className="text-xs text-gray-500 mt-1">Required for In-house production (E/X)</p>
          </div>
        </div>
      </div>

      {/* Lead Times for ATP Calculation */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          Lead Times for ATP Calculation
          <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Important for Availability Check</span>
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          These fields are critical for accurate ATP (Availability to Promise) calculations in Sales Orders. 
          If not maintained, system defaults will be used and warnings will be displayed.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label>Replenishment Lead Time (Days)</Label>
            <Input
              type="number"
              min="0"
              max="999"
              value={mrpData.replenishment_lead_time || ''}
              onChange={(e) => handleFieldChange('replenishment_lead_time', e.target.value)}
              placeholder="Total RLT (optional)"
            />
            <p className="text-xs text-gray-500 mt-1">Total replenishment lead time (overrides individual times)</p>
          </div>
          <div>
            <Label>Planned Delivery Time (Days)</Label>
            <Input
              type="number"
              min="0"
              max="999"
              value={mrpData.planned_delivery_time || ''}
              onChange={(e) => handleFieldChange('planned_delivery_time', e.target.value)}
              placeholder="For external procurement"
            />
            <p className="text-xs text-gray-500 mt-1">Required for External procurement (F)</p>
          </div>
          <div>
            <Label>GR Processing Time (Days)</Label>
            <Input
              type="number"
              min="0"
              max="99"
              value={mrpData.gr_processing_time || ''}
              onChange={(e) => handleFieldChange('gr_processing_time', e.target.value)}
              placeholder="Goods receipt processing"
            />
            <p className="text-xs text-gray-500 mt-1">Time to process goods receipt</p>
          </div>
        </div>
      </div>

      {/* Scheduling & Period */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Scheduling & Period Configuration</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label>Scheduling Margin Key *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={mrpData.scheduling_margin_key_id}
              onChange={(e) => handleFieldChange('scheduling_margin_key_id', e.target.value)}
            >
              <option value="">Select Scheduling Margin Key</option>
              {schedulingMarginKeys.map(smk => (
                <option key={smk.scheduling_margin_key_id} value={smk.scheduling_margin_key_id}>
                  {smk.scheduling_margin_key_id} - {smk.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">For in-house production</p>
          </div>
          <div>
            <Label>Period Indicator *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={mrpData.period_indicator}
              onChange={(e) => handleFieldChange('period_indicator', e.target.value)}
            >
              <option value="">Select Period Indicator</option>
              {periodIndicators.map(pi => (
                <option key={pi.period_indicator_id} value={pi.period_indicator_id}>
                  {pi.period_indicator_id} - {pi.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Planning period unit</p>
          </div>
          <div>
            <Label>Period Indicator for Shelf Life</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={mrpData.period_ind_shelf_life}
              onChange={(e) => handleFieldChange('period_ind_shelf_life', e.target.value)}
            >
              <option value="">Select Period (Optional)</option>
              <option value="D">D - Days</option>
              <option value="M">M - Months</option>
              <option value="W">W - Weeks</option>
              <option value="Y">Y - Years</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Expiry date calculation</p>
          </div>
        </div>
      </div>

      {/* Forecasting Section */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Forecasting Parameters</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label>Forecast Model</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={mrpData.forecast_model_id}
              onChange={(e) => handleFieldChange('forecast_model_id', e.target.value)}
            >
              <option value="">Select Forecast Model</option>
              {forecastModels.map(fm => (
                <option key={fm.forecast_model_id} value={fm.forecast_model_id}>
                  {fm.forecast_model_id} - {fm.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Used for forecasting</p>
          </div>
          <div>
            <Label>Historical Periods</Label>
            <Input
              type="number"
              min="0"
              max="999"
              value={mrpData.hist_periods}
              onChange={(e) => handleFieldChange('hist_periods', e.target.value)}
              placeholder="Optional"
            />
            <p className="text-xs text-gray-500 mt-1">Max 999</p>
          </div>
          <div>
            <Label>Forecast Periods</Label>
            <Input
              type="number"
              min="0"
              max="999"
              value={mrpData.forecast_periods}
              onChange={(e) => handleFieldChange('forecast_periods', e.target.value)}
              placeholder="Optional"
            />
            <p className="text-xs text-gray-500 mt-1">Max 999</p>
          </div>
          <div>
            <Label>Tracking Limit</Label>
            <Input
              type="number"
              min="0"
              max="999"
              value={mrpData.tracking_limit}
              onChange={(e) => handleFieldChange('tracking_limit', e.target.value)}
              placeholder="Optional"
            />
            <p className="text-xs text-gray-500 mt-1">Up to 3 digits</p>
          </div>
          <div>
            <Label>Selection Procedure</Label>
            <Input
              type="text"
              value={mrpData.selection_procedure}
              onChange={(e) => handleFieldChange('selection_procedure', e.target.value)}
              placeholder="Optional"
            />
            <p className="text-xs text-gray-500 mt-1">For automatic forecast model</p>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-medium text-gray-900 mb-1">📋 Note:</p>
        <p>
          Fields marked with * are mandatory. MRP data will be saved when you save the material.
          Make sure to select Plant and Storage Location first before filling other fields.
        </p>
      </div>
    </div>
  );
};

export default MRPView;
